<script>
import Popper from 'popper.js';
import { withDirectives } from 'vue';
import clickOutsideDirective from '@src/utils/directives/clickOutsideDirective';

export default {
  name: 'SbPopover',
  abstract: true,
  props: {

    /**
     * The target of the popover.
     */
    target: {
      required: false,
      default: null,
    },
    /**
     * Used to determine whether or not to bind the popover to the container.
     * necessary for the first paint of the popover as it
     * may not exist yet when using with `v-if`
     */
    active: {
      type: Boolean,
      default: false,
    },

    /**
     * Bounding context to fire the intersection callback on the container.
     * Must of type Element.
     */
    boundariesElement: {
      type: [String, HTMLElement],
      required: false,
      default: null,
    },

    /**
     * Positions the container with respect to its orientation.
     * This follows the same rules as flex-box
     * One of: `(start|center|end)`
     */
    placement: {
      type: String,
      default: 'start',
      validator: (value) => value.match(/(center|start|end)/),
    },
    /**
     * Positions the container around the container element.
     * One of: `(top|bottom|left|right)`
     */
    orientation: {
      type: String,
      default: 'top',
      validator: (value) => value.match(/(top|bottom|left|right)/),
    },
    /**
     * Offset from the container along orientation and placement axes.
     * To configure these, provide an object with the orientation and placement attributes.
     * values can be a percentage, px, or ems.
     */
    offset: {
      type: Object,
      default: () => ({
        orientation: '',
        placement: '',
      }),
    },
    positionFixed: {
      type: Boolean,
      default: false,
    },
    closeOnOutsideClick: {
      type: Boolean,
      default: false,
    },
  },
  emits: [
    /**
     * Emits on click-outside when closeOnOutsideClick is true
     */
    'update:active',
  ],
  data: () => ({
    targetEl: null,
    popperInstance: null,
    shouldRender: false,
    closeGuard: true,
  }),
  computed: {
    placementCalc() {
      let placementString = this.orientation;
      if (this.placement !== 'center') placementString += `-${this.placement}`;
      return placementString;
    },
    defaultOptions() {
      const { orientation: orientationOffset, placement: placementOffset } = this.offset;
      return {
        placement: this.placementCalc,
        positionFixed: this.positionFixed,
        modifiers: {
          preventOverflow: {
            boundariesElement: this.boundariesElement,
            enabled: true,
          },
          offset: {
            offset: `${placementOffset}, ${orientationOffset}`,
          },
        },
      };
    },
  },
  watch: {
    active: {
      immediate: true,
      handler(shouldRender) {
        this.shouldRender = shouldRender;
        if (shouldRender) {
          this.bindPopper();
        }
        this.$nextTick(() => { this.setGuard(!shouldRender); });
      },
    },
  },
  mounted() {
    this.targetEl = this.target || this.$el.parentElement;
    this.bindPopper();
  },
  beforeUnmount() {
    this.killPopper();
  },
  methods: {
    setGuard(val) {
      /**
      * Use this for the click outside directive
      * If we don't set this, then triggering the popover from an outside
      * element closes the popover immediately after opening
      */
      setTimeout(() => {
        this.closeGuard = val;
      }, 0);
    },
    update() {
      if (this.popperInstance) this.popperInstance.update();
    },
    scheduleUpdate() {
      if (this.popperInstance) this.popperInstance.scheduleUpdate();
    },
    bindPopper() {
      this.$nextTick(() => {
        this.createPopper();
      });
    },
    createPopper() {
      if (this.$el.nodeType !== Node.COMMENT_NODE) {
        this.popperInstance = new Popper(this.targetEl, this.$el, this.defaultOptions);
      }
    },
    killPopper() {
      if (this.popperInstance) {
        this.popperInstance.destroy();
        this.popperInstance = null;
      }
    },
    handleClose() {
      if (this.closeGuard) return;
      this.$emit('update:active', false);
    },
    handleClickOutside() {
      if (this.closeOnOutsideClick) {
        this.handleClose();
      }
    },
  },

  render() {
    const defaultSlot = this.$slots.default();
    if (defaultSlot && defaultSlot[0]) {
      return withDirectives(defaultSlot[0], [
        [clickOutsideDirective.directives['click-outside'], this.handleClickOutside],
      ]);
    }
    console.warn('Popover can only render 1 default slot.');
    return undefined;
  },

};
</script>

<style lang='scss' scoped>
@import '@src/components/Popover/style/sb-popover.scss';
</style>
